import { Injectable } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { EupRoutesService, RoutingData } from "@core/eupRoutes.service";
import { LocalStorageService } from "@core/localStorage.service";
import { TimberService } from "@logging/timber.service";
import { ILoggerService, IteroPlatformLogin, Platform } from "@nodeModules/@itero/itero-platform-login";
import { AccessibilityService } from "@shared/accessibility.service";
import { Consts } from "@shared/consts";
import { IShellContext } from "@shared/store/IShellContext";
import { take, filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { GlobalSettingsService } from "@core/globalSettings.service";
import { SessionInfo } from "@nodeModules/@itero/itero-platform-login/models/session-info";
import { jwtDecode } from 'jwt-decode';

@Injectable({
    providedIn: 'root'
})
export class PlatformLoginService {
    private midcLoginRoute = '/login';
	private iteroPlatformLogin: IteroPlatformLogin;
    private initialized$ = new BehaviorSubject<boolean>(false);

    constructor(private readonly router: Router,
        private accessibilityService: AccessibilityService,
        private eupRoutesService: EupRoutesService,
        private timberService: TimberService,
        private localStorageService: LocalStorageService,
        private readonly globalSettingsService: GlobalSettingsService
    ) {}

    initialize() {
        var serviceInput = new IteroPlatformLoginInput();
        serviceInput.clientDiscoveryEndpoint = this.eupRoutesService.clientDiscoveryUrl;
        serviceInput.loggerService = this.timberService;
        serviceInput.smsEndpoint = this.getSmsRootEndpoint();
        serviceInput.loginBffEndpoint = this.localStorageService.getItemOrEmpty<RoutingData>(Consts.Storage.Routes).loginBffUrl;
        this.iteroPlatformLogin = new IteroPlatformLogin(serviceInput);
        this.timberService.info(`Itero platform login package initialized with login service ${serviceInput.loginBffEndpoint}`, { module: 'PlatformLoginService' });
        this.initialized$.next(true);
    }

    iteroPlatformLoginUsed(): boolean {
        const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
        const username = this.getEmailFromAccessToken(sessionInfo?.accessToken);
        const isNewLoginFlow = username ? this.iteroPlatformLogin.isNewLoginFlow(username, Consts.AppId) : false;
        return isNewLoginFlow ?? false;
    }

    setLegacyLogin() {
        const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
        const username = this.getEmailFromAccessToken(sessionInfo?.accessToken);
        if(username)
            this.iteroPlatformLogin.setLegacyLoginFlow(username, Consts.AppId);
    }

    public getEmailFromAccessToken(accessToken: string | undefined): string {
        if (!accessToken) {
            return '';
        }
        try {
            const decoded: any = jwtDecode(accessToken);
            const username = decoded?.preferred_username;
            const sub = decoded?.sub;
            const email = sub && !sub.startsWith('auth0|') ? sub : decoded?.email || '';
            return username || (email.trim() ? email : '');
        } catch (error) {
            this.timberService.error('Error decoding access token for email extraction', { module: 'PlatformLoginService', error });
            return '';
        }
    }

    private getSmsRootEndpoint(): string {
        const authApiUrl = this.eupRoutesService.iTeroWebAuthApiUrl;
        if (!authApiUrl) {
            return authApiUrl;
        }
        try {
            return `${new URL(authApiUrl).origin}/`;
        } catch {
            return authApiUrl;
        }
    }

    updateToken(username: string, sessionId: string, accessToken: string) {
        if (this.iteroPlatformLoginUsed()) {
            this.iteroPlatformLogin.updateToken(username, sessionId, accessToken);
            this.timberService.info(`Token updated in itero platform login package for ${username} and session id ${sessionId}`, { module: 'PlatformLoginService' });
        }
    }

    public navigateToLogin(extras?: NavigationExtras) {
        this.router.navigate([this.midcLoginRoute], extras);
    }

    public navigateToNewLogin() {
        const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
        const idToken = sessionInfo?.idToken;
        const sessionId = sessionInfo?.sessionId;
        const username = this.globalSettingsService.get()?.loginEmail;
		
        if(!idToken || !sessionId || !username) {
            this.timberService.warn('Missing idToken, sessionId, or username in navigateToNewLogin', { module: 'PlatformLoginService' });
            return false;
        }
        this.iteroPlatformLogin.logout(idToken, sessionId, username, Consts.AppId);
        this.iteroPlatformLogin.login();
    }

    public async relogin(returnUri: string, loginHint: string, platformContext?: IShellContext): Promise<void> {
        await this.iteroPlatformLogin.relogin(Consts.AppId, returnUri, loginHint, platformContext as any);
    }

    public navigateToExternalLogin(baseUrl: string, queryParams: string) {
        const url = new URL(this.midcLoginRoute, baseUrl);
        url.search = queryParams;
        this.accessibilityService.redirectTo(url.toString());
    }

    async logout(idToken: string, sessionId: string, username: string): Promise<void> {
        this.timberService.info(`Platform login package logout called for session ${sessionId}`, { module: 'PlatformLoginService' });
        await this.iteroPlatformLogin.logout(idToken, sessionId, username, Consts.AppId);
    }

    public getLoginRoutes(): string[] {
        return [this.midcLoginRoute];
    }
	
	public async canUserAccessPlatform(platform: Platform): Promise<boolean> {
		const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
		const accessToken = sessionInfo?.accessToken;
		
		if(!platform || !accessToken) {
			this.timberService.warn('Missing platform or accessToken in canUserAccessPlatform', { module: 'PlatformLoginService' });
			return false;
		}

		return this.iteroPlatformLogin.canUserAccessPlatform(platform, accessToken);
	}

    public getLoginInfo(loginHint: string, state: string, sessionId: string, loginCallback: (loginResponse: LoginResponse) => void) {
        this.initialized$.pipe(filter(ready => ready === true), take(1)).subscribe(() => {
            this.iteroPlatformLogin.getLoginInfo(Consts.AppId, loginHint, sessionId, state, loginCallback);
        });
    }

    public async getUserProvider(accessToken: string): Promise<string | null> {
        if (!accessToken) {
            this.timberService.warn('Missing accessToken in getUserProvider', { module: 'PlatformLoginService' });
            return null;
        }

        const res = await this.iteroPlatformLogin.getUserProvider(accessToken);
        return res ? res.provider : null;
    }
}

export class IteroPlatformLoginInput {
    clientDiscoveryEndpoint: string;
    loggerService: ILoggerService;
    smsEndpoint: string;
    loginBffEndpoint: string;
}

export class LoginResponse
{
    context: IShellContext;
    sessionInfo: SessionInfo;
    returnUri: string;
}